/** @jsxImportSource @emotion/react */
import { css, SerializedStyles } from '@emotion/react';
import * as React from 'react';
import dtw from '../../assets/icons/daypass.svg';
import fnf from '../../assets/icons/fnf.svg';
import importGuestIcon from '../../assets/icons/save.svg';
import { CONFIG_KEY } from '../../auth/config/configKey';
import ConfigHide from '../../auth/ConfigHide';
import { EntitlementType } from '../../entitlementTypes/entitlementType';
import StatusButton from '../../forms/elements/StatusButton';
import { isNilOrEmpty } from '../../language/module';
import { EntitlementHotlist } from '../entitlements/models';
import { getActiveEntitlementTypes } from '../entitlements/module';
import { getRequiredWaivers, hasValidWaiver } from '../entitlements/waivers/module';
import { importGuestDisabledMessage, redeemEntitlementDisabledMessage, redeemFriendsAndFamilyDisabledMessage } from '../featureDisabling';
import { GuestDetails } from '../guestDetails';
import { NO_RADIUS } from './guestCardStyles';
import ImmutableMap from '../../language/ImmutableMap';
import { getResource } from '../../language/resources/getResource';
import { IAppState } from '../../core/redux/IAppState';
import { useSelector } from 'react-redux';


const styles = {
  root: css`
    display: flex; 
    flex-direction: row;
    justify-content: space-around;
  `,
  dtw:  css`
    margin: 0 5px;
  `,
  fnf:  css`
    margin: 0 5px;
  `,
};


type Props = {
  guest: GuestDetails;
  loadingImportGuest: boolean;
  importGuestSuccess: boolean;
  passEntitlementId: number;
  entitlementHotlists?: ImmutableMap<EntitlementHotlist[]>;
  allEntitlementTypes: ImmutableMap<EntitlementType>;
  onDtwButtonClick: () => void;
  onFnfButtonClick: () => void;
  onImportGuestClick: () => void;
  enableCustomerImport: boolean;
  rootCss?: SerializedStyles;
};

/**
 * SecondaryActions: a component that renders the Guest Card's secondary actions (Day Pass, Friends & Family ticket,
 * Import Guest). These actions don't have colorful borders, but they still need a few things to handle enable/disable
 * etc.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
const SecondaryActions: React.FC<Props> = props => {
  const hasSignedWaivers = React.useMemo(
    () => hasValidWaiver(
      props.guest,
      getRequiredWaivers(
        getActiveEntitlementTypes(props.guest, props.allEntitlementTypes),
      ),
    ),
    [props.guest, props.allEntitlementTypes],
  );
  const resources = useSelector((state: IAppState) => state.locale);

  const disableEntitlement = React.useMemo(
    () => redeemEntitlementDisabledMessage(
      resources,
      props.passEntitlementId,
      props.guest,
      hasSignedWaivers,
      props.entitlementHotlists,
    ),
    [resources, props.passEntitlementId, props.guest, hasSignedWaivers, props.entitlementHotlists],
  );

  const disableFriendsAndFamily = React.useMemo(
    () => redeemFriendsAndFamilyDisabledMessage(
      resources,
      props.passEntitlementId,
      props.guest,
      props.entitlementHotlists,
    ), 
    [resources, props.passEntitlementId, props.guest, props.entitlementHotlists], 
  )

  const disableImport = React.useMemo(
    () => importGuestDisabledMessage(
      resources,
      props.passEntitlementId,
      props.importGuestSuccess ? [props.guest.masterGuestId] : [],
      props.guest,
      props.enableCustomerImport
    ),
    [resources, props.passEntitlementId, props.importGuestSuccess, props.guest, props.enableCustomerImport],
  );

  return (
    <div css={css`${styles.root} ${props.rootCss}`}>
      <ConfigHide configPath={CONFIG_KEY.ENABLE_DTW} secondaryConfigPath={CONFIG_KEY.MANAGE_DAYS}>
        <StatusButton
          handleClick={props.onDtwButtonClick}
          image={dtw}
          borderRadius={NO_RADIUS}
          borderColor='transparent'
          isLoading={false}
          toolTipText={getResource(resources, 'SECONDARY_ACTIONS_REDEEM_PASS')}
          borderWidth={0}
          height={60}
          width={60}
          disable={!isNilOrEmpty(disableEntitlement)}
          disableToolTip={disableEntitlement}
          rootCss={styles.dtw}
        />
      </ConfigHide>
      <ConfigHide configPath={CONFIG_KEY.ENABLE_FRIENDS_AND_FAMILY}>
        <StatusButton
          handleClick={props.onFnfButtonClick}
          image={fnf}
          borderRadius={NO_RADIUS}
          borderColor='transparent'
          isLoading={false}
          toolTipText={getResource(resources, 'SECONDARY_ACTIONS_REDEEM_FNF')}
          borderWidth={0}
          height={60}
          width={60}
          disable={!isNilOrEmpty(disableFriendsAndFamily)}
          disableToolTip={disableFriendsAndFamily}
          rootCss={styles.fnf}
        />
      </ConfigHide>
      <StatusButton
        handleClick={props.onImportGuestClick}
        image={importGuestIcon}
        borderRadius={NO_RADIUS}
        borderColor='transparent'
        isLoading={props.loadingImportGuest || false}
        disable={!isNilOrEmpty(disableImport)}
        disableToolTip={disableImport}
        toolTipText={getResource(resources, 'SECONDARY_ACTIONS_IMPORT_TOOLTIP')}
        borderWidth={0}
        height={65}
        width={65}
      />
    </div>
  );
};

export default SecondaryActions;
